// src/pages/Dashboard/DashboardView.jsx
import React, { useEffect, useState } from "react";
import { Typography, Card, Table, Segmented, Grid } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { fetchDashboardStats } from "../../store/commonThunks/dashboard";
import {
  selectDashboardStats,
  selectDashboardLoading,
} from "../../store/commonSelectors/lookupsSelectors";

import MetricCard from "./components/MetricCard";
import {
  LineChartOutlined,
  RiseOutlined,
  FundOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import "./dashboard.css";

import SalesTrendChart from "./components/SalesTrendChart";
import DashboardCharts from "./components/DashboardCharts";
import DashboardProducts from "./components/DashboardProducts";
import TodaySummary from "./components/TodaySummary";
import CountTiles from "./components/CountTiles";
import DashboardQuickActions from "./components/DashboardQuickActions";
import BalanceSummary from "./components/BalanceSummary";
import useHrTiles from "./components/useHrTiles";
import useCrmTiles from "./components/useCrmTiles";
import { blueTone } from "./dashboardTheme";
import { selectAuthInfo } from "../Auth/authSelectors";

import { getSalesAmountApi } from "../../apiservice/dashboard";
import { currentDateStartTime, currentDateEndTime, formatToCompanyTimezone } from "../../lib/functions";
import dayjs from "dayjs";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "This Month", value: "month" },
];

const DashboardView = () => {
  const dispatch = useDispatch();
  const stats = useSelector(selectDashboardStats);
  const loading = useSelector(selectDashboardLoading);
  const authInfo = useSelector(selectAuthInfo);

  // HR snapshot is limited to the roles the HR Payroll dashboard shows it to
  // (every *_hr and *_admin role). ForthCRM plans don't show it.
  const isForthCrmPlan = /^forthcrm/i.test(String(authInfo?.subscription || ""));
  const canSeeHr =
    !isForthCrmPlan &&
    (authInfo?.userInfo?.user_role_names || "")
      .split(",")
      .some((role) => /_(hr|admin)$/.test(role.trim()));

  // CRM snapshot follows the sidebar's rule for showing the CRM module.
  const canSeeCrm =
    String(authInfo?.subscription || "").toLowerCase() === "supererp" ||
    (!authInfo?.disabled_modules?.includes("crm") &&
      authInfo?.tab?.some((t) => t.tab === "crm"));

  const moduleTiles = [...useHrTiles(canSeeHr), ...useCrmTiles(canSeeCrm)];

  const [overviewFilter, setOverviewFilter] = useState("today");
  const [salesTrendData, setSalesTrendData] = useState([]);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    // Pass filter to your thunk
    dispatch(fetchDashboardStats(overviewFilter));
  }, [dispatch, overviewFilter]);

  // Last 7 Months helper
  const getLast7MonthsDates = () => {
    const months = [];
    for (let i = 6; i >= 0; i--) {
      const start = dayjs().subtract(i, "month").startOf("month");
      const end = dayjs().subtract(i, "month").endOf("month");

      months.push({
        label: start.format("MMMM"),
        fromDate: currentDateStartTime(start),
        toDate: currentDateEndTime(end),
      });
    }
    return months;
  };

  useEffect(() => {
    const fetchSalesTrend = async () => {
      const months = getLast7MonthsDates();
      const res = await getSalesAmountApi({
        ranges: months,
        page: "dashboard",
      });
      setSalesTrendData(res.data || []);
    };
    fetchSalesTrend();
  }, []);

  const compareLabel = overviewFilter === "month" ? "vs last month" : "vs yesterday";

  const columns = [
    {
      title: "Date", dataIndex: "date", key: "date", render: (v) =>
        v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (val) => (
        <span className="erp-amount">
          {val?.toFixed(2)}
          <span className="erp-unit">{stats.currency}</span>
        </span>
      ),
    },
  ];

  return (
    <div className="erp-dashboard">
      {/* HEADER */}
      <div className="erp-header">
        <Title level={isMobile ? 4 : 3} className="erp-title">
          Dashboard
        </Title>

        {/* Shortcuts to the major "create new" pages, gated by module + permission */}
        <DashboardQuickActions />

        {/* The period switch drives the KPI cards only */}
        <Segmented
          className="erp-period"
          options={PERIOD_OPTIONS}
          value={overviewFilter}
          onChange={(val) => setOverviewFilter(val)}
        />
      </div>

      <div className="erp-body">
        {/* KPI cards */}
        <div className="erp-kpi-grid">
          <MetricCard
            title="Sales"
            icon={<LineChartOutlined />}
            tone={blueTone(0, 4)}
            value={stats.sales}
            currency={stats.currency}
            trend={stats.salesTrend || 0}
            diff={stats.salesDiff || 0}
            compareLabel={compareLabel}
          />
          <MetricCard
            title="Gross Profit"
            icon={<RiseOutlined />}
            tone={blueTone(1, 4)}
            value={stats.grossProfit}
            currency={stats.currency}
            trend={stats.grossProfitTrend || 0}
            diff={stats.grossProfitDiff || 0}
            compareLabel={compareLabel}
          />
          <MetricCard
            title="Net Profit"
            icon={<FundOutlined />}
            tone={blueTone(2, 4)}
            value={stats.netProfit}
            currency={stats.currency}
            trend={stats.netProfitTrend || 0}
            diff={stats.netProfitDiff || 0}
            compareLabel={compareLabel}
          />
          <MetricCard
            title="Active Users"
            icon={<TeamOutlined />}
            tone={blueTone(3, 4)}
            value={stats.activeUsers}
            trend={stats.activeUsersTrend || 0}
            diff={stats.activeUsersDiff || 0}
            compareLabel={compareLabel}
          />
        </div>

        {/* HR + CRM snapshot as one group of count tiles */}
        {moduleTiles.length > 0 && (
          <CountTiles tiles={moduleTiles} columns={moduleTiles.length > 4 ? 8 : 4} />
        )}

        {/* Sales trend + balance summary */}
        <div className="erp-grid two-one">
          <SalesTrendChart
            data={salesTrendData || []}
            currency={stats.currency}
            isMobile={isMobile}
          />

          <BalanceSummary
            cash={stats.cashBalance}
            bank={stats.bankBalance}
            receivables={stats.receivables}
            payables={stats.payables}
            currency={stats.currency}
          />
        </div>

        {/* Monthly charts + today summary */}
        <div className="erp-grid two-one">
          <DashboardCharts currency={stats.currency} isMobile={isMobile} />
          <TodaySummary currency={stats.currency} />
        </div>

        {/* Products + recent monthly table */}
        <div className="erp-grid two">
          <DashboardProducts
            fromDate={stats.fromDate}
            toDate={stats.toDate}
            currency={stats.currency}
          />

          <Card className="erp-card" variant="borderless">
            <div className="erp-card-head">
              <div>
                <div className="erp-card-title">Recent Monthly Sales vs Purchase</div>
                <div className="erp-card-sub">Latest 10 entries · selected period</div>
              </div>
            </div>
            <Table
              className="erp-table"
              size="small"
              columns={columns}
              dataSource={stats.recentTransactions || []}
              pagination={false}
              loading={loading}
              rowKey="id"
              scroll={{ x: "max-content", y: 320 }}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
